/**
 * FhirCarePlanWidget — Care plan summary for subject dashboard (DRR-321)
 *
 * Shows active care plans from ServicePlan data.
 * Uses useServicePlansBySubjectQuery for real data.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
  Progress,
} from '@mantine/core';
import { IconClipboardList, IconAlertCircle, IconCalendar } from '@tabler/icons-react';
import { useServicePlansBySubjectQuery, PlanStatus } from '@ferp/core/generated/graphql';
import type { WidgetProps } from '@ferp/core';

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  DRAFT: 'gray',
  PENDING_APPROVAL: 'yellow',
  PENDING_REVIEW: 'orange',
  REJECTED: 'red',
  EXPIRED: 'gray',
  SUPERSEDED: 'blue',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  PENDING_REVIEW: 'Pending Review',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  SUPERSEDED: 'Superseded',
};

function formatDate(timestamp?: number | null): string {
  if (!timestamp) return 'Not set';
  return new Date(timestamp * 1000).toLocaleDateString();
}

export function FhirCarePlanWidget({ subjectActorId, tenantId }: WidgetProps) {
  const { data, loading, error } = useServicePlansBySubjectQuery({
    variables: {
      subjectId: subjectActorId ?? '',
      tenantId: tenantId ?? '',
      status: PlanStatus.ACTIVE,
      limit: 10,
    },
    skip: !subjectActorId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const plans = useMemo(() => {
    return data?.servicePlansBySubject ?? [];
  }, [data]);

  if (loading && plans.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading care plans.
      </Alert>
    );
  }

  if (plans.length === 0) {
    return (
      <Alert icon={<IconClipboardList size={16} />} color="blue" variant="light">
        No active care plans for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconClipboardList size={16} color="var(--mantine-color-blue-6)" />
        <Text fw={600} size="sm">Care Plans</Text>
        <Badge size="sm" color="green">{plans.length} Active</Badge>
      </Group>

      <ScrollArea.Autosize mah={250}>
        <Stack gap="xs">
          {plans.map((plan) => {
            const domainCount = plan.activityDomains?.length ?? 0;

            return (
              <Card key={plan.id} withBorder padding="xs" radius="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>
                      {plan.planType}
                    </Text>
                    <Group gap="xs" mt={4}>
                      <Badge size="xs" variant="light" color="blue">
                        v{plan.version}
                      </Badge>
                      {domainCount > 0 && (
                        <Badge size="xs" variant="outline" color="gray">
                          {domainCount} domain{domainCount !== 1 ? 's' : ''}
                        </Badge>
                      )}
                      {plan.effectiveStart && (
                        <Group gap={2}>
                          <IconCalendar size={10} color="gray" />
                          <Text size="xs" c="dimmed">
                            {formatDate(plan.effectiveStart)}
                          </Text>
                        </Group>
                      )}
                    </Group>
                    {domainCount > 0 && (
                      <Progress
                        size="xs"
                        value={100}
                        color="green"
                        mt="xs"
                      />
                    )}
                  </Box>
                  <Badge size="xs" color={STATUS_COLOR[plan.status] ?? 'gray'}>
                    {STATUS_LABEL[plan.status] ?? plan.status}
                  </Badge>
                </Group>
              </Card>
            );
          })}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
